import {useEffect, useRef, useState} from 'react';

import type Cropper from 'react-easy-crop';
import type {Area, MediaSize, Point} from 'react-easy-crop';

import {
  getThumbnailsInBrowser,
  getVideoDurationInBrowser,
} from '@edume/bento/videoUtil.browser';

import {
  calculateMinZoom,
  clampCrop,
  clampCroppedAreaPixels,
} from './videoCropperUtils';

const THUMBNAIL_COUNT = 15;
export const CROP_AREA = {width: 225, height: 400};

export type UseVideoCropperProps = {
  sourceFile: Blob | null;
  initialCroppedAreaPixels?: Area;
  initialTrim?: {
    start: number;
    end: number;
  };
};

export function useVideoCropper({
  sourceFile,
  initialTrim,
  initialCroppedAreaPixels,
}: UseVideoCropperProps) {
  const cropperRef = useRef<Cropper>(null);
  const [videoRef, setVideoRef] = useState<HTMLVideoElement | null>(null);
  const [fileUrl, setFileUrl] = useState('');
  const [mediaSize, setMediaSize] = useState<MediaSize | null>(null);
  const [videoThumbnails, setVideoThumbnails] = useState<string[]>([]);
  const [thumbnailsLoading, setThumbnailsLoading] = useState(true);

  const [videoStart, setVideoStart] = useState(initialTrim?.start || 0);
  const [videoEnd, setVideoEnd] = useState(initialTrim?.end || 100000);
  const [videoDuration, setVideoDuration] = useState(100000);
  const [videoCurrentTime, setVideoCurrentTime] = useState(1);

  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area>({
    width: 0,
    height: 0,
    x: 0,
    y: 0,
  });
  const [croppedAreaPixelsOriginal, setCroppedAreaPixelsOriginal] =
    useState<Area>({
      width: 0,
      height: 0,
      x: 0,
      y: 0,
    });
  const [crop, setCrop] = useState<Point>({x: 0, y: 0});
  const [zoom, setZoom] = useState<number>(1);
  const [minZoom, setMinZoom] = useState<number>(1);
  const [restrictPosition, setRestrictPosition] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopVideo = () => {
    if (!timeoutRef.current || !videoRef) {
      return;
    }
    clearTimeout(timeoutRef.current);
    videoRef.pause();
  };

  const startVideo = () => {
    stopVideo();
    if (!videoRef) {
      return;
    }
    videoRef.currentTime = videoStart;
    const playPromise = videoRef.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Auto-play was prevented
        // Do nothing
      });
    }

    checkTime();
  };

  const checkTime = () => {
    if (!videoRef) {
      return;
    }
    setVideoCurrentTime(videoRef.currentTime);
    if (videoRef.currentTime >= videoEnd) {
      startVideo();
    } else {
      timeoutRef.current = setTimeout(checkTime, 50);
    }
  };

  useEffect(() => {
    if (sourceFile) {
      getVideoDurationInBrowser({file: sourceFile}).then((duration: number) => {
        setVideoDuration(Number(duration));
        if (!initialTrim?.end) {
          setVideoEnd(Number(duration));
        }
      });
      setFileUrl(URL.createObjectURL(sourceFile));

      getThumbnailsInBrowser(sourceFile, THUMBNAIL_COUNT).then((data) => {
        setVideoThumbnails(data);
        setThumbnailsLoading(false);
      });
    }

    return () => {
      stopVideo();
    };
  }, [sourceFile, initialTrim]);

  useEffect(() => {
    if (videoRef) {
      startVideo();
    }
  }, [videoRef, videoStart, videoEnd]);

  const onMediaLoaded = (mediaSizeProp: MediaSize) => {
    if (cropperRef.current?.videoRef?.current) {
      cropperRef.current.videoRef.current.pause();
    }
    setVideoRef(cropperRef.current?.videoRef?.current || null);
    setMediaSize(mediaSizeProp);
    setMinZoom(calculateMinZoom(mediaSizeProp, CROP_AREA));
  };

  const onCropAreaChange = (croppedArea: Area) => {
    if (!mediaSize) {
      return;
    }
    const newCrop = clampCrop(crop, croppedArea, mediaSize, CROP_AREA);
    setCrop(newCrop);
    setRestrictPosition(croppedArea.height < 100);
  };

  const onCropComplete = (_: Area, croppedAreaPixelsProp: Area) => {
    if (!mediaSize) {
      return;
    }
    setCroppedAreaPixelsOriginal(croppedAreaPixelsProp);
    setCroppedAreaPixels(
      clampCroppedAreaPixels(croppedAreaPixelsProp, mediaSize)
    );
  };

  const getCropResult = () => {
    stopVideo();
    if (mediaSize) {
      return {
        videoStart,
        videoEnd,
        croppedAreaPixels,
        croppedAreaPixelsOriginal,
        mediaSize,
      };
    }
    return null;
  };

  return {
    cropperRef,
    videoRef,
    fileUrl,
    thumbnailsLoading,
    videoThumbnails,
    videoDuration,
    videoCurrentTime,
    videoStart,
    videoEnd,
    crop,
    zoom,
    minZoom,
    restrictPosition,
    setVideoStart,
    setVideoEnd,
    setCrop,
    setZoom,
    onMediaLoaded,
    onCropAreaChange,
    onCropComplete,
    getCropResult,
    initialCroppedAreaPixels,
  };
}
